import { Button, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ForcePaymentCheckDialog from '../../components/invoices/ForcePaymentCheckDialog'
import CancelFiltersButton from '../../components/common/CancelFiltersButton'
import CustomContainer from '../../components/common/CustomContainer'
import CustomDatePicker from '../../components/common/CustomDatePicker'
import DateRangePicker from '../../components/common/DateRangePicker'
import PaymentStateFilter from '../../components/common/PaymentStateFilter'
import TabPanel from '../../components/common/TabPanel'
import TextSearchField from '../../components/common/TextSearchField'
import ClientSelect from '../../components/invoices/ClientSelect'
import InvoiceTable from '../../components/invoices/InvoiceTable'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { ClientManager } from '../../services/api/ClientManager'
import { InvoiceManager } from '../../services/api/InvoiceManager'
import { ClientBriefDto } from '../../services/api/sharedTypes/Client'
import { PaymentState } from '../../services/api/sharedTypes/Common'
import { InvoiceBriefDto, InvoiceIssuerBriefDto } from '../../services/api/sharedTypes/Invoice'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { convertToSearchString, openInNewTab } from '../../utils/global-utils'
import {
    convertNumberToInvoiceTab,
    getEmptyCreateInvoiceIssuerObject,
    getInvoiceTab,
    getInvoiceTabNumber,
} from '../../utils/invoice-utils'
import InvoiceIssuer from './InvoiceIssuer'

const Invoices: React.FC = () => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [selectedTabValue, setSelectedTabValue] = useState<number>(
        getInvoiceTabNumber(getInvoiceTab(location.hash.replace('#', ''))) ?? 0
    )
    const [loadingPageData, setLoadingPageData] = useState<boolean>(false)
    const [loadingIssuerData, setLoadingIssuerData] = useState<boolean>(false)
    const [loadingClientSelect, setLoadingClientSelect] = useState<boolean>(true)
    const [invoices, setInvoices] = useState<InvoiceBriefDto[]>()
    const [filteredInvoices, setFilteredInvoices] = useState<InvoiceBriefDto[]>()
    const [invoiceIssuers, setInvoicesIssuers] = useState<InvoiceIssuerBriefDto[]>()
    const [selectedPaymentState, setSelectedPaymentState] = useState<PaymentState[]>([])
    const [expireByDate, setExpireByDate] = useState<Date | undefined | null>(null)
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [searchedValue, setSearchedValue] = useState<string>('')
    const [selectedClientId, setSelectedClientId] = useState<string>('')
    const [selectedClientName, setSelectedClientName] = useState<string | null>(null)
    const [clientList, setClientList] = useState<ClientBriefDto[] | undefined>()
    const [forceCheckOpen, setForceCheckOpen] = useState(false)

    const handleChangeTabValue = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, newValue: number) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (event.metaKey || event.button === 1 || event.ctrlKey) {
            openInNewTab(RouteConstants.invoices + '#' + convertNumberToInvoiceTab(newValue))
        } else {
            setSelectedTabValue(newValue)
            location.hash = convertNumberToInvoiceTab(newValue)
        }
    }

    const removeInvoice = async (id: string) => {
        if (!invoices) return
        setLoadingIssuerData(true)
        try {
            await InvoiceManager.getManager().deleteInvoiceAsync(id)
            dispatch(displayMessage({ message: 'Fakturace smazána', type: 'success' }))
            setInvoices(invoices.filter((i) => i.id !== id))
        } catch (error) {
            setLoadingIssuerData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingIssuerData(false)
    }

    const createInvoiceIssuer = async () => {
        setLoadingIssuerData(true)
        try {
            const invoiceIssuersRes = await InvoiceManager.getManager().createInvoiceIssuerAsync(
                getEmptyCreateInvoiceIssuerObject()
            )
            if (invoiceIssuersRes) {
                setSelectedTabValue(1)
                invoiceIssuers
                    ? setInvoicesIssuers([...invoiceIssuers, invoiceIssuersRes])
                    : setInvoicesIssuers([invoiceIssuersRes])
                dispatch(displayMessage({ message: 'Fakturace vytvořena', type: 'success' }))
            }
        } catch (error) {
            setLoadingIssuerData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingIssuerData(false)
    }

    const changeInvoiceIssuerInfo = async (newInvoiceIssuer: InvoiceIssuerBriefDto) => {
        if (!invoiceIssuers) return dispatch(displayMessage({ message: 'Fakturaci nelze upravit', type: 'error' }))
        const newInvoiceIssuers = invoiceIssuers.map((invoiceIssuer) => {
            if (invoiceIssuer.id === newInvoiceIssuer.id) {
                return newInvoiceIssuer
            }
            return invoiceIssuer
        })
        setInvoicesIssuers(newInvoiceIssuers)
    }

    const deleteInvoiceIssuer = async (id: string) => {
        setLoadingIssuerData(true)
        try {
            await InvoiceManager.getManager().deleteInvoiceIssuerAsync(id)
            invoiceIssuers && setInvoicesIssuers(invoiceIssuers.filter((issuer) => issuer.id !== id))
        } catch (error) {
            setLoadingIssuerData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        dispatch(displayMessage({ message: 'Fakturace smazána', type: 'success' }))
        setLoadingIssuerData(false)
    }

    const updateInvoiceIssuer = async (id: string, invoiceIssuer: InvoiceIssuerBriefDto) => {
        setLoadingIssuerData(true)
        try {
            const invoiceIssuerRes = await InvoiceManager.getManager().updateInvoiceIssuerAsync(id, invoiceIssuer)
            if (invoiceIssuerRes) {
                changeInvoiceIssuerInfo(invoiceIssuerRes)
                dispatch(displayMessage({ message: 'Fakturace upravena', type: 'success' }))
                setLoadingIssuerData(false)
                return invoiceIssuerRes
            }
        } catch (error) {
            setLoadingIssuerData(false)
            dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const uploadInvoiceIssuerImage = async (id: string, image?: File) => {
        setLoadingIssuerData(true)
        try {
            const invoiceRes = await InvoiceManager.getManager().uploadInvoiceIssuerImageAsync(id, image)
            if (invoiceRes) {
                dispatch(displayMessage({ message: image ? 'Obrázek přidán' : 'Obrázek smazán', type: 'success' }))
                setLoadingIssuerData(false)
                return invoiceRes
            }
        } catch (error) {
            setLoadingIssuerData(false)
            dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const getClientList = async () => {
        try {
            setLoadingClientSelect(true)
            const clientRes = await ClientManager.getManager().getAllClientsAsync()
            if (!clientRes) throw new Error('Klienti nejsou načteni')
            setClientList(clientRes.clients)
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingClientSelect(false)
        }
        setLoadingClientSelect(false)
    }

    const getInvoiceIssuerList = async () => {
        setLoadingIssuerData(true)
        try {
            const invoiceIssuersRes = await InvoiceManager.getManager().getInvoiceIssuerListAsync()
            if (invoiceIssuersRes) {
                setInvoicesIssuers(invoiceIssuersRes.invoiceIssuers)
            }
        } catch (error) {
            setLoadingIssuerData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingIssuerData(false)
    }

    const fetchData = async () => {
        setLoadingPageData(true)
        try {
            const invoicesRes = await InvoiceManager.getManager().getInvoiceListAsync()
            if (invoicesRes) {
                setInvoices(invoicesRes.invoices)
                setFilteredInvoices(invoicesRes.invoices)
            }
        } catch (error) {
            setLoadingPageData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }

        setLoadingPageData(false)
    }

    //Get invoices from server
    useEffect(() => {
        fetchData()
        getClientList()
        getInvoiceIssuerList()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const setFilteredClientsByEventDate = async () => {
            if (!invoices || invoices.length <= 0) return
            setLoadingPageData(true)
            const invoiceManager = InvoiceManager.getManager()
            if (dateTo && dateFrom) {
                try {
                    if (isNaN(dateTo.getTime()) || isNaN(dateFrom.getTime())) return setLoadingPageData(false)
                    const invoiceRes = await invoiceManager?.getInvoiceListFilteredAsync(
                        dateFrom.toISOString(),
                        dateTo.toISOString()
                    )
                    if (invoiceRes) {
                        setFilteredInvoices(invoiceRes.invoices)
                    }
                } catch (error) {
                    setLoadingPageData(false)
                    return dispatch(displayMessage({ message: error, type: 'error' }))
                }
            } else {
                const invoicesRes = await invoiceManager.getInvoiceListAsync()
                if (invoicesRes) {
                    setInvoices(invoicesRes.invoices)
                    setFilteredInvoices(invoicesRes.invoices)
                }
            }
            setLoadingPageData(false)
        }

        setFilteredClientsByEventDate()
    }, [dateFrom, dateTo])

    const invoicesForTable = filteredInvoices
        ?.filter((invoice) => {
            if (searchedValue) {
                return (
                    convertToSearchString(invoice.clientName).includes(convertToSearchString(searchedValue)) ||
                    convertToSearchString(invoice.issuerName).includes(convertToSearchString(searchedValue)) ||
                    convertToSearchString(invoice.invoiceNumber.toString()).includes(convertToSearchString(searchedValue))
                )
            }
            return true
        })
        .filter((invoice) => {
            if (selectedPaymentState) {
                return selectedPaymentState.length === 0 || selectedPaymentState.includes(invoice.paymentState)
            }
            return true
        })
        .filter((invoice) => {
            if (selectedClientName) {
                return selectedClientName.includes(invoice.clientName)
            }
            return true
        })
        .filter((invoice) => {
            if (expireByDate) {
                return new Date(invoice.dueDate).getTime() <= expireByDate.getTime()
            }
            return true
        })

    if (loadingPageData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
                <Typography variant='h5'>Faktury</Typography>
                <Stack direction='row' gap={theme.spacing(1)}>
                    <Button variant='outlined' onClick={() => setForceCheckOpen(true)}>
                        Zkontrolovat platby
                    </Button>
                    <Button variant='contained' onClick={createInvoiceIssuer}>
                        + Přidat fakturaci
                    </Button>
                </Stack>
            </Stack>

            <Tabs value={selectedTabValue}>
                <Tab label='Přehled' onMouseDown={(e) => handleChangeTabValue(e, 0)} />
                <Tab label='Fakturace' onMouseDown={(e) => handleChangeTabValue(e, 1)} />
            </Tabs>

            <TabPanel value={selectedTabValue} index={0}>
                <Stack direction='row' gap={theme.spacing(3)} mt={theme.spacing(1)} mb={theme.spacing(1)} overflow='auto'>
                    <div style={{ paddingTop: theme.spacing(2), maxWidth: 220, minWidth: 180 }}>
                        <ClientSelect
                            clientId={selectedClientId ?? null}
                            onChange={(e) => setSelectedClientId(e)}
                            onChangeName={(e) => setSelectedClientName(e)}
                            clientList={clientList}
                            isLoading={loadingClientSelect}
                        />
                    </div>

                    <PaymentStateFilter paymentState={selectedPaymentState} onChange={setSelectedPaymentState} />

                    <div style={{ paddingTop: theme.spacing(2), maxWidth: 180, minWidth: 120 }}>
                        <CustomDatePicker
                            label='Splatnost od'
                            value={expireByDate ?? null}
                            onChange={(e) => setExpireByDate(e)}
                        />
                    </div>
                    <DateRangePicker
                        labelFrom='Vystavení od'
                        labelTo='Vystavení do'
                        dateTo={dateTo}
                        dateFrom={dateFrom}
                        onSetDateFrom={setDateFrom}
                        onSetDateTo={setDateTo}
                    />
                    <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                    {
                        searchedValue || dateFrom || dateTo || selectedPaymentState.length > 0 || selectedClientId || expireByDate ? (
                            <CancelFiltersButton onClick={
                                () => {
                                    setSearchedValue('')
                                    setDateFrom(null)
                                    setDateTo(null)
                                    setSelectedPaymentState([])
                                    setSelectedClientId('')
                                    setSelectedClientName('')
                                    setExpireByDate(null)
                                }
                            }
                            />
                        ) : null
                    }
                </Stack>
                {invoicesForTable && <InvoiceTable invoices={invoicesForTable} onDelete={removeInvoice} />}
            </TabPanel>

            <TabPanel value={selectedTabValue} index={1} style={{ overflow: 'auto' }}>
                {invoiceIssuers &&
                    invoiceIssuers.map((invoiceIssuer, index) => (
                        <InvoiceIssuer
                            key={invoiceIssuer.id}
                            index={index + 1}
                            id={invoiceIssuer.id}
                            isLoading={loadingIssuerData}
                            onDelete={deleteInvoiceIssuer}
                            onUpdate={updateInvoiceIssuer}
                            uploadInvoiceIssuerImage={uploadInvoiceIssuerImage}
                        />
                    ))}
            </TabPanel>
            <ForcePaymentCheckDialog
                open={forceCheckOpen}
                onClose={() => setForceCheckOpen(false)}
                onSuccess={fetchData}
            />
        </CustomContainer>
    )
}

export default Invoices
